import { holdExpiryFrom, type ComparisonColumn, type ComparisonView, type HoldRowView } from '@nest-recruit/shared';

/**
 * What the screen shows the moment someone acts (CLAUDE.md §5a rule 1), before the server
 * answers with the whole comparison. Provisional only: these never decide who may do
 * what — the server's answer replaces them, and a refusal rolls them back.
 */

const PENDING_REFERENCE = 'Creating…';
/** The selection's offer id until the server's answer lands (S23 links to the offer only after). */
export const PENDING_OFFER_ID = 'pending';

function withColumn(view: ComparisonView, applicationId: string, change: (column: ComparisonColumn) => ComparisonColumn): ComparisonView {
  return { ...view, columns: view.columns.map((column) => (column.applicationId === applicationId ? change(column) : column)) };
}

function withoutColumn(view: ComparisonView, applicationId: string): ComparisonView {
  return { ...view, columns: view.columns.filter((column) => column.applicationId !== applicationId) };
}

function withHold(view: ComparisonView, applicationId: string, change: (row: HoldRowView) => HoldRowView | null): ComparisonView {
  return { ...view, holds: view.holds.flatMap((row) => (row.applicationId === applicationId ? [change(row)].filter((next): next is HoldRowView => next !== null) : [row])) };
}

function takesAPlace(view: ComparisonView): ComparisonView {
  return { ...view, places: { ...view.places, selected: view.places.selected + 1, left: Math.max(0, view.places.left - 1) } };
}

export function selected(view: ComparisonView, applicationId: string, byName: string, now: Date): ComparisonView {
  const next = withColumn(view, applicationId, (column) => ({
    ...column,
    status: 'selected',
    selectBlockedReason: 'Already selected.',
    selection: { offerId: PENDING_OFFER_ID, offerReference: PENDING_REFERENCE, selectedAt: now.toISOString(), selectedByName: byName, canUndo: false },
  }));
  return takesAPlace(next);
}

export function held(view: ComparisonView, applicationId: string, now: Date): ComparisonView {
  return withColumn(view, applicationId, (column) => ({
    ...column,
    status: 'held',
    selectBlockedReason: 'On hold.',
    hold: { holdId: 'pending', expiresAt: holdExpiryFrom(now).toISOString(), state: 'active', extensionCount: 0, heldFromLabel: column.stageLabel, placedAt: now.toISOString(), placedByName: null },
  }));
}

export function eliminated(view: ComparisonView, applicationId: string, reasonLabel: string, byName: string, now: Date): ComparisonView {
  const column = view.columns.find((row) => row.applicationId === applicationId);
  const next = withoutColumn(view, applicationId);
  if (!column) return next;
  return { ...next, eliminated: [{ applicationId, candidateName: column.candidate.fullName, reasonLabel, note: null, byName, at: now.toISOString() }, ...next.eliminated] };
}

export function removed(view: ComparisonView, applicationId: string): ComparisonView {
  const column = view.columns.find((row) => row.applicationId === applicationId);
  const next = withoutColumn(view, applicationId);
  if (!column) return next;
  return { ...next, addable: [...next.addable, { applicationId, candidateName: column.candidate.fullName, stageLabel: column.stageLabel }] };
}

export function restored(view: ComparisonView, applicationId: string): ComparisonView {
  return { ...view, eliminated: view.eliminated.filter((row) => row.applicationId !== applicationId) };
}

export function added(view: ComparisonView, applicationId: string): ComparisonView {
  return { ...view, addable: view.addable.filter((row) => row.applicationId !== applicationId) };
}

export function releasedToOffer(view: ComparisonView, applicationId: string, byName: string, now: Date): ComparisonView {
  return selected(withHold(view, applicationId, () => null), applicationId, byName, now);
}

export function releasedToPool(view: ComparisonView, applicationId: string): ComparisonView {
  return withoutColumn(withHold(view, applicationId, () => null), applicationId);
}

export function extended(view: ComparisonView, applicationId: string, now: Date): ComparisonView {
  const expiresAt = holdExpiryFrom(now).toISOString();
  return withHold(view, applicationId, (row) => ({ ...row, hold: { ...row.hold, expiresAt, state: 'active', extensionCount: row.hold.extensionCount + 1 } }));
}
